import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Row, Col, Button } from 'react-bootstrap';
import { omit as _omit } from 'lodash';

import { formStatusDispatchHelper, formReset } from 'actions/FormStatusActions';
import { addNotification } from 'actions/NotificationsActions';
import { generateMetaTags } from 'helpers/metaTags';

import EntityStateUpdater from 'component/EntityStateUpdater';

import Breadcrumb from 'components/layout/Breadcrumb';
import Tabs from 'components/layout/Tabs';

import ClientProfile from 'components/layout/client/ClientProfile';
import ClientFinancial from 'components/layout/client/ClientFinancial';
import ClientExternalAccounts from 'components/layout/client/ClientExternalAccounts';
import ClientStripeAccounts from 'components/layout/client/ClientStripeAccounts';

import DocMeta from 'components/DocMeta';
import Entity from 'components/Entity';
import Data from 'components/Data';

import { entityMetaMapping, ROLE_ADMIN } from 'js/constants';
import may from 'js/security';

import FormErrors from 'components/forms/FormErrors';

class MyCompany extends EntityStateUpdater {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
    };

    this.entity = new Entity();

    if (props.meta[entityMetaMapping.client] !== undefined && !props.meta[entityMetaMapping.client].pending) {
      this.entity.setMeta(props.meta, entityMetaMapping.client);
    }
  }

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      meta,
      data,
      formStatus,
      doNotify,
    } = this.props;

    // client data was saved and there was no error
    if (prevProps.formStatus.pending && !formStatus.pending && !formStatus.hasError) {
      this.setState({ entityChanged: false });
      doNotify(1, messages.company_data_saved);
    }

    if (prevProps.data.pending && !data.pending) {
      this.entity.setData(data.data);
      if (this.entity.getMetaLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }

    if (
      meta[entityMetaMapping.client] !== undefined &&
      !meta[entityMetaMapping.client].pending &&
      !this.entity.getMetaLoaded()
    ) {
      this.entity.setMeta(meta, entityMetaMapping.client);
      if (this.entity.getDataLoaded()) {
        this.setState({ entityLoaded: true, entity: this.entity });
      }
    }
  };

  formSubmit = e => {
    e.preventDefault();

    this.setState(
      prevState => ({ entity: prevState.entity.validate() }),
      () => {
        const { onSubmit } = this.props;
        const { entity } = this.state;

        if (entity.isValid) {
          // ommit unnecessary data
          const data = _omit(entity.processedData, ['projectLang']);
          onSubmit(data);
        }
      }
    );
  };

  getTabHeaderList = () => {
    const {
      intl: { messages },
      user,
    } = this.props;
    const result = [];

    result.push(messages.profile);
    result.push(messages.financial);

    if (may(ROLE_ADMIN, user.roles)) {
      result.push(messages.external_accounts);
      result.push(messages.stripe_accounts);
    }

    return result;
  };

  getTabContentList = () => {
    const { formStatus, user } = this.props;
    const { entity, entityLoaded } = this.state;
    const result = [];

    const disabled = formStatus.pending;
    const sharedProps = { entityLoaded, entity, changeEntity: this.changeEntity, disabled };

    result.push(<ClientProfile {...sharedProps} />);
    // financial settings (bank account, legal)
    result.push(<ClientFinancial {...sharedProps} />);
    // mobile.de, vamoso, otr, etc. accounts - only when admin
    if (may(ROLE_ADMIN, user.roles)) {
      result.push(<ClientExternalAccounts {...sharedProps} />);
      result.push(<ClientStripeAccounts {...sharedProps} />);
    }

    return result;
  };

  getTabKeepMounted = () => {
    const keepMounted = [];
    return keepMounted;
  };

  render() {
    const {
      intl: { messages },
      appIntl,
      data,
      formStatus,
    } = this.props;

    return (
      <DocMeta tags={generateMetaTags(messages.meta.my_company)}>
        <div class="padding-25 sm-padding-10">
          <FormErrors formStatus={formStatus} />

          <form method="put" onSubmit={this.formSubmit}>
            <Data dataName="client" /> {/* fetch meta data */}
            <Data dataName="myParent" url="/api/clients/parent.json" data={data} />
            <Breadcrumb
              crumbs={[
                { link: appIntl.urlPrefix + 'account', name: 'Dashboard' },
                { link: appIntl.urlPrefix + 'account/settings', name: messages.settings },
                { link: appIntl.urlPrefix + 'account/settings/myCompany', name: messages.my_company },
              ]}
              className="p-t-0"
            />
            <Row>
              <Col xs={12}>
                <div class="text-right m-b-10">
                  <Button
                    bsSize="lg"
                    disabled={formStatus.pending}
                    type="submit"
                    bsStyle={this.state.entityChanged ? 'primary' : 'default'}
                  >
                    <i class={'fa-fw ' + (formStatus.pending ? ' fa fa-circle-notch fa-spin' : 'fal fa-save')} />{' '}
                    {messages.save}
                  </Button>
                </div>
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Tabs
                  tabs={this.getTabHeaderList()}
                  tabContent={this.getTabContentList()}
                  keepMounted={this.getTabKeepMounted()}
                />
              </Col>
            </Row>
          </form>
        </div>
      </DocMeta>
    );
  }
}

const mapStateToProps = state => {
  return {
    user: state.login.user,
    appIntl: state.intl,
    data: state.data.myParent,
    meta: state.entityMeta,
    formStatus: state.formStatus.myParentEdit,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    onSubmit: args => {
      formStatusDispatchHelper('myParentEdit', '/api/clients/parent.json', args, 'put', dispatch);
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(MyCompany)
);
